<div class="tab-panel tab-panel--history">
  <!-- The admin screens' controls, laid out for a panel a fraction of their width: fixed rows of two
       rather than a wrapping bar, so the controls line up instead of reflowing raggedly as their
       placeholders change length.

       Who, Label and Event type are multi-selects: a value picked is a value that can be unticked,
       which a single-select could not offer (its "All ..." is only a placeholder, not an option).
       The date pickers carry their own clear icon, shown only once a date is set, so the bar stays
       uncluttered until a filter is actually applied. The button below clears everything. -->
  <div class="history-filter-bar">
    <div class="history-filter-bar__row">
      <p-iconfield class="history-filter-bar__search" iconPosition="left">
        <p-inputicon class="pi pi-search"></p-inputicon>
        <input type="text" pInputText [(ngModel)]="searchText" (ngModelChange)="onFilterInput()" placeholder="Search history" aria-label="Search history" />
      </p-iconfield>

      <!-- Always rendered, disabled when there is nothing to clear: hiding it made the one control
           that undoes a filter appear only once you had already applied one. -->
      <button
        type="button"
        class="history-filter-bar__clear"
        [disabled]="!hasActiveFilters"
        (click)="onResetFilters()"
        pTooltip="Clear all filters"
        tooltipPosition="left"
        aria-label="Clear all filters"
      >
        <i class="pi pi-times"></i>
      </button>
    </div>

    <div class="history-filter-bar__row">
      <!-- Who. Offers only people who have actually changed this item, which is the only set that
           can return rows - not the project's whole membership.

           The tooltip lives on this wrapping span, not the p-select itself: PrimeNG gives a
           disabled control `pointer-events: none`, which blocks the hover pTooltip listens for on
           whatever element it is bound to - a disabled control's own tooltip would never fire. The
           span sits outside that rule, so it keeps receiving the hover the control itself no longer
           can. Empty/null when enabled, and pTooltip renders nothing for that (see actorFilterTooltip). -->
      <span class="history-filter-bar__tooltip-target" [pTooltip]="actorFilterTooltip" tooltipPosition="top">
        <p-multiSelect
          [options]="actorOptions"
          [(ngModel)]="selectedActorIds"
          (onChange)="onFilterChange()"
          optionLabel="label"
          optionValue="id"
          placeholder="All users"
          ariaLabel="Filter by user"
          [maxSelectedLabels]="1"
          selectedItemsLabel="{0} users"
          appendTo="body"
          [disabled]="actorFilterDisabled"
        ></p-multiSelect>
      </span>

      <!-- Where. The label a change was recorded in. Same reason as Who above for hanging the
           tooltip off a wrapping span rather than the p-select. -->
      <span class="history-filter-bar__tooltip-target" [pTooltip]="labelFilterTooltip" tooltipPosition="top">
        <p-multiSelect
          [options]="labelOptions"
          [(ngModel)]="selectedLabelIds"
          (onChange)="onFilterChange()"
          optionLabel="label"
          optionValue="id"
          placeholder="All labels"
          ariaLabel="Filter by label"
          [maxSelectedLabels]="1"
          selectedItemsLabel="{0} labels"
          [filter]="labelOptions.length > 10"
          filterPlaceHolder="Search label"
          appendTo="body"
          [disabled]="labelFilterDisabled"
        ></p-multiSelect>
      </span>
    </div>

    <!-- The dropdowns above are an aid, not the record itself - a failure to load them must not look
         like there is simply nothing to pick. Its own retry, separate from the main list's: the list
         may already be loaded and correct. -->
    @if (filterOptionsFailed) {
    <div class="history-state history-state--error history-filter-bar__options-error" role="alert">
      <i class="pi pi-exclamation-triangle"></i>
      <span>Couldn't load filter options.</span>
      <button type="button" class="history-retry" (click)="onRetryFilterOptions()">Retry</button>
    </div>
    }

    <div class="history-filter-bar__row">
      <!-- What kind of change. A fixed list rather than one fetched from the item's history, the same
           reason the scope chips above are static: the set of possible event types does not depend
           on what this item happens to have recorded. -->
      <!-- Same reason as Who/Where above for hanging the tooltip off a wrapping span: PrimeNG's own
           disabled styling blocks pointer events on the control itself, which would otherwise take
           the hover pTooltip needs down with it. -->
      <span class="history-filter-bar__tooltip-target" [pTooltip]="actionTypeFilterTooltip" tooltipPosition="top">
        <p-multiSelect
          [options]="actionTypeFilters"
          [(ngModel)]="selectedActionTypes"
          (onChange)="onFilterChange()"
          optionLabel="label"
          optionValue="id"
          placeholder="All event types"
          ariaLabel="Filter by event type"
          [maxSelectedLabels]="1"
          selectedItemsLabel="{0} event types"
          appendTo="body"
          [disabled]="actionTypeFilterDisabled"
        ></p-multiSelect>
      </span>
    </div>

    <div class="history-filter-bar__row">
      <!-- Bounded against each other so an impossible range cannot be picked in the first place. -->
      <p-datepicker
        [(ngModel)]="fromDate"
        (onSelect)="onFilterChange()"
        (onClear)="onFilterChange()"
        (onClearClick)="onFilterChange()"
        (onInput)="onFilterInput()"
        [showClear]="true"
        [showButtonBar]="true"
        [maxDate]="toDate ?? undefined"
        dateFormat="dd/mm/yy"
        placeholder="From"
        ariaLabel="Filter from date"
        appendTo="body"
      ></p-datepicker>
      <p-datepicker
        [(ngModel)]="toDate"
        (onSelect)="onFilterChange()"
        (onClear)="onFilterChange()"
        (onClearClick)="onFilterChange()"
        (onInput)="onFilterInput()"
        [showClear]="true"
        [showButtonBar]="true"
        [minDate]="fromDate ?? undefined"
        dateFormat="dd/mm/yy"
        placeholder="To"
        ariaLabel="Filter to date"
        appendTo="body"
      ></p-datepicker>
    </div>
  </div>

  <div class="history-toolbar">
    <div class="history-filters" role="group" aria-label="Filter history by type">
      @for (filter of scopeFilters; track filter.label) {
      <button type="button" class="history-chip" [class.history-chip--active]="activeScope === filter.id" [attr.aria-pressed]="activeScope === filter.id" (click)="onScopeSelected(filter.id)">
        {{ filter.label }}
      </button>
      }
    </div>

    <!-- Exports the whole history for this item under the filter on screen, not just the pages
         loaded so far - the file is built server-side for exactly that reason. Icon-only: the
         tooltip carries the label, the same way the filter bar's clear button does. -->
    <button
      type="button"
      class="history-export"
      [disabled]="isExporting || isLoading || rows.length === 0"
      (click)="onExport()"
      [pTooltip]="isExporting ? 'Exporting…' : 'Download the full history for this item as CSV'"
      tooltipPosition="left"
      aria-label="Export history as CSV"
    >
      @if (isExporting) {
      <i class="pi pi-spin pi-spinner"></i>
      } @else {
      <i class="pi pi-download"></i>
      }
    </button>
  </div>

  <!-- Reserves its own row rather than replacing the table, so paging to the next page reads as a
       brief in-place update instead of the whole pane blanking and reappearing on every click. -->
  @if (isLoading && rows.length > 0) {
  <p-progressbar mode="indeterminate" [style]="{ height: '3px' }"></p-progressbar>
  }

  @if (isLoading && rows.length === 0) {
  <div class="history-state" aria-live="polite">
    <i class="pi pi-spin pi-spinner"></i>
    <span>Loading history…</span>
  </div>
  }

  <!-- The error state keeps the pane usable rather than blanking it: the filters stay, and the
       failed load can be retried without reopening the tab. -->
  @if (lastError && !isLoading) {
  <div class="history-state history-state--error" role="alert">
    <i class="pi pi-exclamation-triangle"></i>
    <span>Couldn't load history. {{ lastError }}</span>
    <button type="button" class="history-retry" (click)="onRetry()">Retry</button>
  </div>
  }

  <!-- Its own line, not the load error: the list on screen is untouched by a failed export, and the
       remedy is to try the download again rather than to reload. -->
  @if (exportError) {
  <div class="history-state history-state--error" role="alert">
    <i class="pi pi-exclamation-triangle"></i>
    <span>{{ exportError }}</span>
    <button type="button" class="history-retry" [disabled]="isExporting" (click)="onExport()">Try again</button>
  </div>
  } @if (isEmpty) {
  <div class="history-state history-state--empty">
    <span>No history recorded for this item.</span>
  </div>
  } @if (rows.length > 0) {
  <div class="history-table-scroll">
    <!-- p-table purely as a row renderer here - [value] is already the one server page the store
         holds, so none of its own filtering/sorting/pagination features apply; dataKey gives it
         the same row identity trackByRowKey used to give the old @for loop.
         tableStyleClass, not styleClass - styleClass lands on p-table's own outer wrapper,
         tableStyleClass on the actual <table> it renders, which is where .history-table's
         width/border-collapse rules and the e2e selectors ("history-table tbody tr...") need it. -->
    <p-table [value]="rows" dataKey="key" tableStyleClass="history-table">
      <ng-template pTemplate="header">
        <tr>
          <th scope="col" class="history-table__when">When</th>
          <th scope="col" class="history-table__who">Who</th>
          @if (showLabelColumn) {
          <th scope="col" class="history-table__label">Label</th>
          }
          <th scope="col" class="history-table__what">What changed</th>
          <th scope="col" class="history-table__from">From</th>
          <th scope="col" class="history-table__to">To</th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-row>
        <!-- Rows of one entry are grouped visually: only the first repeats When and Who, so a save
             that changed three fields reads as one event rather than three unrelated ones. -->
        <!-- The entry id is on every row of that entry, so a test can tell "one save that changed
             three fields" from "three saves". -->
        <tr class="history-row" [class.history-row--entry-start]="row.startsEntry" [attr.data-entry-id]="row.entry.id">
          <!-- `row.when` is null when the stored timestamp will not parse. Piping it anyway would
               throw during change detection and blank the whole tab, so the row loses its date and
               keeps everything else. -->
          <!-- Date and time on separate lines. Letting the stamp wrap keeps this column narrow
               instead of setting the table's width from the longest timestamp in it. -->
          <td class="history-table__when" [attr.title]="row.entry.occurredAt">
            @if (row.startsEntry && row.when) {
            <span class="history-row__date">{{ row.when | date: 'dd MMM yyyy' }}</span>
            <span class="history-row__time">{{ row.when | date: 'HH:mm' }}</span>
            }
          </td>
          <td class="history-table__who">
            @if (row.startsEntry) {
            <!-- Hovering the name shows the email it was recorded against. No tooltip at all when
                 there is no email (an integration or service account), rather than one echoing the
                 name already on screen. -->
            <span class="history-row__actor" [class.history-row__actor--has-email]="actorEmail(row.entry)" [attr.title]="actorEmail(row.entry)">{{ actorName(row.entry) }}</span>
            @if (externalSource(row.entry)) {
            <span class="history-row__source">via {{ externalSource(row.entry) }}</span>
            }
            }
          </td>
          <!-- Shown once per entry, same as Who: every change in one save happened under the same
               label, so repeating it per row would say nothing a second glance doesn't already know.
               Struck through when the label is archived right now - a live fact, not part of the
               permanent record, so it reflects the label's current state rather than its state when
               the change was recorded. -->
          @if (showLabelColumn) {
          <td class="history-table__label">
            @if (row.startsEntry) {
            <span class="history-row__label" [class.history-row__label--archived]="row.entry.isLabelArchived" [attr.title]="row.entry.isLabelArchived ? 'This label is archived' : null">{{ labelName(row.entry) }}</span>
            }
          </td>
          }

          <!-- [Transaction] [Field] - "Updated Name", "Added Label", "Removed Attachment" - rather
               than a sentence. The verbs line up down the column, which is what makes a long trail
               scannable. -->
          @if (row.actionOnly) {
          <td class="history-table__what" colspan="3">
            <span class="history-row__transaction">{{ transaction(row.entry) }}</span>
            <span class="history-row__field">{{ subjectFor(row) }}</span>
          </td>
          } @else {
          <td class="history-table__what">
            <span class="history-row__transaction">{{ transaction(row.entry) }}</span>
            <span class="history-row__field">{{ subjectFor(row) }}</span>
          </td>
          <td class="history-table__from">
            <span class="history-row__old">{{ row.oldValue }}</span>
          </td>
          <td class="history-table__to">
            <span class="history-row__new">{{ row.newValue }}</span>
            @if (row.truncated) {
            <span class="history-row__truncated" title="Stored value was shortened">(truncated)</span>
            }
          </td>
          }
        </tr>
      </ng-template>
    </p-table>
  </div>
  } @if (rows.length > 0) {
  <!-- Always rendered once there is a page to show, even a single one - the nav buttons disable
       themselves rather than the row disappearing, the same way the admin log screen's paginator
       stays on screen at "1 of 1" instead of vanishing. That is what makes paging visible as a
       feature rather than something that only appears once an item happens to have 26+ entries.
       One server page on screen at a time, fetched fresh on every click - not the whole history
       accumulated client-side, which is what the old Load more button did.

       Built from plain PrimeNG parts rather than one packaged paginator component, so each half
       can match a different reference exactly: the size control is p-select with no styling of
       our own, the same as the admin Users tab's own rows-per-page dropdown; the label, report
       and First/Prev/Next/Last layout match the dashboard project list's mat-paginator. Neither
       off-the-shelf paginator (PrimeNG's own, Angular Material's) offers both at once. -->
  <div class="history-pager">
    <div class="history-pager__size">
      <label id="historyPageSizeLabel" class="history-pager__size-label">Rows:</label>
      <p-select [options]="pageSizeOptions" [(ngModel)]="pageSize" (onChange)="onPageSizeChange()" ariaLabelledBy="historyPageSizeLabel" appendTo="body"></p-select>
    </div>
    <div class="history-pager__nav">
      <span class="history-pager__label">{{ rangeLabel }}</span>
      <button type="button" class="history-pager__button" [disabled]="!hasPreviousPage || isLoading" (click)="onFirstPage()" pTooltip="First page" tooltipPosition="top" aria-label="First page">
        <i class="pi pi-angle-double-left"></i>
      </button>
      <button type="button" class="history-pager__button" [disabled]="!hasPreviousPage || isLoading" (click)="onPreviousPage()" pTooltip="Previous page" tooltipPosition="top" aria-label="Previous page">
        <i class="pi pi-angle-left"></i>
      </button>
      <button type="button" class="history-pager__button" [disabled]="!hasNextPage || isLoading" (click)="onNextPage()" pTooltip="Next page" tooltipPosition="top" aria-label="Next page">
        <i class="pi pi-angle-right"></i>
      </button>
      <button type="button" class="history-pager__button" [disabled]="!hasNextPage || isLoading" (click)="onLastPage()" pTooltip="Last page" tooltipPosition="top" aria-label="Last page">
        <i class="pi pi-angle-double-right"></i>
      </button>
    </div>
  </div>
  }
</div>
